import React, { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProjectsProvider } from "@/contexts/ProjectsContext";
import { PersonasProvider } from "@/contexts/PersonasContext";
import { MarketFitProvider } from "@/contexts/MarketFitContext";
import { CampaignBlueprintsProvider } from "@/contexts/CampaignBlueprintsContext";
import { LinkedInAuthorityProvider } from "@/contexts/LinkedInAuthorityContext";
import { ContentPillarsProvider } from "@/contexts/ContentPillarsContext";
import { CampaignAssetsProvider } from "@/contexts/CampaignAssetsContext";
import { CRMReportingProvider } from "@/contexts/CRMReportingContext";
import { OrgProvider } from "@/contexts/OrgContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import { Toaster } from "sonner";
// Sonner's runtime CSS injection is disabled (see craco.config.js / sonner-patcher-loader.js).
// Import the stylesheet statically so it lands in the main CSS bundle instead.
import "sonner/dist/styles.css";

// Critical path — eagerly loaded (needed on first paint for public/auth pages)
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";

// App routes — lazy loaded (only authenticated users ever reach these)
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const NewProject = React.lazy(() => import("@/pages/NewProject"));
const Workspace = React.lazy(() => import("@/pages/Workspace"));
const Billing = React.lazy(() => import("@/pages/Billing"));
const BillingSuccess = React.lazy(() => import("@/pages/BillingSuccess"));
const BillingCancel = React.lazy(() => import("@/pages/BillingCancel"));
const Account = React.lazy(() => import("@/pages/Account"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const ResetPassword = React.lazy(() => import("@/pages/ResetPassword"));
const ForgotPassword = React.lazy(() => import("@/pages/ForgotPassword"));
const Personas = React.lazy(() => import("@/pages/Personas"));
const PersonaDetail = React.lazy(() => import("@/pages/PersonaDetail"));
const MarketFitDetail = React.lazy(() => import("@/pages/MarketFitDetail"));
const MarketFit = React.lazy(() => import("@/pages/MarketFit"));
const CampaignBlueprintsIndex = React.lazy(() => import("@/pages/CampaignBlueprintsIndex"));
const CampaignBlueprints = React.lazy(() => import("@/pages/CampaignBlueprints"));
const CampaignBlueprintDetail = React.lazy(() => import("@/pages/CampaignBlueprintDetail"));
const LinkedInAuthorityIndex = React.lazy(() => import("@/pages/LinkedInAuthorityIndex"));
const LinkedInAuthorityDetail = React.lazy(() => import("@/pages/LinkedInAuthorityDetail"));
const ContentPillarsIndex = React.lazy(() => import("@/pages/ContentPillarsIndex"));
const ContentPillarDetail = React.lazy(() => import("@/pages/ContentPillarDetail"));
const CampaignAssetsIndex = React.lazy(() => import("@/pages/CampaignAssetsIndex"));
const CampaignAssetBatchDetail = React.lazy(() => import("@/pages/CampaignAssetBatchDetail"));
const CRMReportingPacksIndex = React.lazy(() => import("@/pages/CRMReportingPacks"));
const CRMReportingPackDetail = React.lazy(() => import("@/pages/CRMReportingPackDetail"));
const SafetyReviewsIndex = React.lazy(() => import("@/pages/SafetyReviews"));
const SafetyReviewDetail = React.lazy(() => import("@/pages/SafetyReviewDetail"));
const AuditLog = React.lazy(() => import("@/pages/AuditLog"));
const OrgSettings = React.lazy(() => import("@/pages/OrgSettings"));
const AcceptInvite = React.lazy(() => import("@/pages/AcceptInvite"));
const EnterpriseSettings = React.lazy(() => import("@/pages/EnterpriseSettings"));

// Secondary public pages — lazy loaded (not on critical render path)
const Share = React.lazy(() => import("@/pages/Share"));
const Features = React.lazy(() => import("@/pages/Features"));
const Pricing = React.lazy(() => import("@/pages/Pricing"));
const About = React.lazy(() => import("@/pages/About"));
const FAQ = React.lazy(() => import("@/pages/FAQ"));
const Privacy = React.lazy(() => import("@/pages/Privacy"));

function RedirectIfAuthed({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProjectsProvider>
          <PersonasProvider>
          <MarketFitProvider>
          <CampaignBlueprintsProvider>
          <LinkedInAuthorityProvider>
          <ContentPillarsProvider>
          <CampaignAssetsProvider>
          <CRMReportingProvider>
          <OrgProvider>
          <BrowserRouter>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
                <Route path="/register" element={<RedirectIfAuthed><Register /></RedirectIfAuthed>} />
                <Route path="/verify" element={<VerifyEmail />} />
                <Route path="/share/:token" element={<Share />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/projects/new" element={<ProtectedRoute><NewProject /></ProtectedRoute>} />
                <Route path="/projects/:id/edit" element={<ProtectedRoute><NewProject editing /></ProtectedRoute>} />
                <Route path="/projects/:id/workspace" element={<ProtectedRoute><Workspace /></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
                <Route path="/billing/cancel" element={<ProtectedRoute><BillingCancel /></ProtectedRoute>} />
                <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                <Route path="/personas" element={<ProtectedRoute><Personas /></ProtectedRoute>} />
                <Route path="/personas/:id" element={<ProtectedRoute><PersonaDetail /></ProtectedRoute>} />
                <Route path="/market-fit" element={<ProtectedRoute><MarketFit /></ProtectedRoute>} />
                <Route path="/projects/:projectId/market-fit/:recordId" element={<ProtectedRoute><MarketFitDetail /></ProtectedRoute>} />
                <Route path="/campaign-blueprints" element={<ProtectedRoute><CampaignBlueprintsIndex /></ProtectedRoute>} />
                <Route path="/projects/:projectId/campaign-blueprints" element={<ProtectedRoute><CampaignBlueprints /></ProtectedRoute>} />
                <Route path="/projects/:projectId/campaign-blueprints/:blueprintId" element={<ProtectedRoute><CampaignBlueprintDetail /></ProtectedRoute>} />
                <Route path="/linkedin-authority" element={<ProtectedRoute><LinkedInAuthorityIndex /></ProtectedRoute>} />
                <Route path="/projects/:projectId/linkedin-authority-packs/:packId" element={<ProtectedRoute><LinkedInAuthorityDetail /></ProtectedRoute>} />
                <Route path="/content-pillars" element={<ProtectedRoute><ContentPillarsIndex /></ProtectedRoute>} />
                <Route path="/projects/:projectId/content-pillar-sets/:setId" element={<ProtectedRoute><ContentPillarDetail /></ProtectedRoute>} />
                <Route path="/campaign-assets" element={<ProtectedRoute><CampaignAssetsIndex /></ProtectedRoute>} />
                <Route path="/projects/:projectId/campaign-asset-batches/:batchId" element={<ProtectedRoute><CampaignAssetBatchDetail /></ProtectedRoute>} />
                <Route path="/crm-reporting" element={<ProtectedRoute><CRMReportingPacksIndex /></ProtectedRoute>} />
                <Route path="/projects/:projectId/crm-reporting-packs/:packId" element={<ProtectedRoute><CRMReportingPackDetail /></ProtectedRoute>} />
                <Route path="/safety-reviews" element={<ProtectedRoute><SafetyReviewsIndex /></ProtectedRoute>} />
                <Route path="/projects/:projectId/safety-reviews/:reviewId" element={<ProtectedRoute><SafetyReviewDetail /></ProtectedRoute>} />
                <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
                <Route path="/org-settings" element={<ProtectedRoute><OrgSettings /></ProtectedRoute>} />
                <Route path="/org-invites/:token/accept" element={<AcceptInvite />} />
                <Route path="/enterprise" element={<ProtectedRoute><EnterpriseSettings /></ProtectedRoute>} />
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          </OrgProvider>
          </CRMReportingProvider>
          </CampaignAssetsProvider>
          </ContentPillarsProvider>
          </LinkedInAuthorityProvider>
          </CampaignBlueprintsProvider>
          </MarketFitProvider>
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{ style: { background: "#141414", border: "1px solid #262626", color: "#fff", fontFamily: "IBM Plex Mono, monospace", fontSize: "12px" } }}
          />
          </PersonasProvider>
        </ProjectsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
